import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Grid,
    Typography,
    TextField,
    Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { MODAL_BORDER_RADIUS } from '../../utils/dateUtils';

const EditItemModal = ({
    open,
    onClose,
    onUpdate,
    onDelete,
    onEnterMoveMode,
    onPreview,
    formState,
    handleFormChange,
    handleContentTypeChange,
    handleContentSelection,
    handleCollectionSelection,
    simulationData,
    courseData,
    collectionData,
    featureSwitchesApiData,
    style
}) => {
    const [simulationType, setSimulationType] = useState(null);

    const simulationsEnabled = featureSwitchesApiData?.enableJourneyBuilderSimulations !== false;
    const coursesEnabled = featureSwitchesApiData?.enableJourneyBuilderCourses !== false;
    const bothEnabled = simulationsEnabled && coursesEnabled;
    const onlySimulations = simulationsEnabled && !coursesEnabled;
    const onlyCourses = !simulationsEnabled && coursesEnabled;
    const noneEnabled = !simulationsEnabled && !coursesEnabled;

    useEffect(() => {
        if (!open) {
            setSimulationType(null);
            return;
        }

        if (onlySimulations && formState.contentType !== 'Simulation') {
            const syntheticEvent = {
                target: { value: 'Simulation' }
            };
            handleContentTypeChange(syntheticEvent);
        } else if (onlyCourses && formState.contentType !== 'Course') {
            const syntheticEvent = {
                target: { value: 'Course' }
            };
            handleContentTypeChange(syntheticEvent);
        }
    }, [open, onlySimulations, onlyCourses]);

    useEffect(() => {
        if (!open) {
            setSimulationType(null);
            return;
        }

        if (formState.contentType === 'Simulation') {
            if (simulationType === null) {
                if (formState.selectedContentName === 'Random Simulation' || formState.isRandomSimulation) {
                    setSimulationType('Random');
                } else if (formState.selectedContentName && formState.selectedContentName !== 'Random Simulation') {
                    setSimulationType('Manual');
                }
            }
        } else if (formState.contentType === 'Course') {
            setSimulationType(null);
        }
    }, [open, formState.contentType]);

    const handleContentTypeButtonClick = (contentType) => {
        if (contentType === 'Course' && !coursesEnabled) return;
        if (contentType === 'Simulation' && !simulationsEnabled) return;

        setSimulationType(null);
        const syntheticEvent = {
            target: { value: contentType }
        };
        handleContentTypeChange(syntheticEvent);
    };

    const handleSimulationTypeClick = (type) => {
        setSimulationType(type);
        if (type === 'Random') {
            const syntheticEvent = {
                target: { value: 'Random Simulation' }
            };
            handleContentSelection(syntheticEvent);
        } else {
            const resetEvent = {
                target: { value: '' }
            };
            handleContentSelection(resetEvent);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: MODAL_BORDER_RADIUS,
                    ...style
                },
                sx: { bgcolor: 'background.default' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Edit Item</Typography>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        disabled={!formState.contentId}
                        onClick={onPreview}
                        startIcon={<VisibilityIcon />}
                    >
                        Preview
                    </Button>
                </Box>
            </DialogTitle>
            <DialogContent>
                {noneEnabled ? (
                    <Box sx={{ py: 3 }}>
                        <Typography variant="body1" color="text.secondary" align="center">
                            No content types are enabled for Journey Builder. Please contact your administrator.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={3}>
                        {/* Content Type Selection Buttons */}
                        <Grid item xs={12} sx={{ mb: -0.1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Content type
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                <Button
                                    variant={formState.contentType === 'Course' ? 'contained' : 'outlined'}
                                    color={formState.contentType === 'Course' ? 'warning' : 'inherit'}
                                    onClick={() => handleContentTypeButtonClick('Course')}
                                    disabled={!coursesEnabled}
                                    sx={{ flex: 1, mr: 1 }}
                                >
                                    Course
                                </Button>
                                <Button
                                    variant={formState.contentType === 'Simulation' ? 'contained' : 'outlined'}
                                    color={formState.contentType === 'Simulation' ? 'primary' : 'inherit'}
                                    onClick={() => handleContentTypeButtonClick('Simulation')}
                                    disabled={!simulationsEnabled}
                                    sx={{ flex: 1, ml: 1 }}
                                >
                                    Simulation
                                </Button>
                            </Box>
                        </Grid>

                        {/* Course Selection */}
                        {formState.contentType === 'Course' && coursesEnabled && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    size="small"
                                    options={courseData || []}
                                    getOptionLabel={(option) => option.courseName}
                                    value={courseData?.find(c => c.courseName === formState.selectedContentName) || null}
                                    onChange={(event, newValue) => {
                                        const syntheticEvent = {
                                            target: {
                                                name: 'selectedContentName',
                                                value: newValue?.courseName || ''
                                            }
                                        };
                                        handleContentSelection(syntheticEvent);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Course"
                                            required
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.courseName === value?.courseName}
                                />
                            </Grid>
                        )}

                        {/* Simulation Type Selection */}
                        {formState.contentType === 'Simulation' && simulationsEnabled && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Simulation selection
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                    <Button
                                        variant={simulationType === 'Manual' ? 'contained' : 'outlined'}
                                        color={simulationType === 'Manual' ? 'primary' : 'inherit'}
                                        onClick={() => handleSimulationTypeClick('Manual')}
                                        sx={{ flex: 1, mr: 1 }}
                                    >
                                        Manual
                                    </Button>

                                    {featureSwitchesApiData.enableRandomSimulation ? (
                                        <Button
                                            variant={simulationType === 'Random' ? 'contained' : 'outlined'}
                                            color={simulationType === 'Random' ? 'primary' : 'inherit'}
                                            onClick={() => handleSimulationTypeClick('Random')}
                                            sx={{ flex: 1, ml: 1 }}
                                        >
                                            Random
                                        </Button>
                                    ) : (
                                        <Box sx={{ flex: 1, ml: 1 }} />
                                    )}
                                </Box>
                            </Grid>
                        )}

                        {/* Manual Simulation Selection */}
                        {formState.contentType === 'Simulation' && simulationType === 'Manual' && simulationsEnabled && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    size="small"
                                    options={simulationData || []}
                                    getOptionLabel={(option) => option.simulationName}
                                    value={simulationData?.find(s => s.simulationName === formState.selectedContentName && formState.selectedContentName !== 'Random Simulation') || null}
                                    onChange={(event, newValue) => {
                                        const syntheticEvent = {
                                            target: {
                                                name: 'selectedContentName',
                                                value: newValue?.simulationName || ''
                                            }
                                        };
                                        handleContentSelection(syntheticEvent);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Simulation"
                                            required
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.simulationName === value?.simulationName}
                                />
                            </Grid>
                        )}

                        {/* Random Simulation Collection Selection */}
                        {formState.contentType === 'Simulation' && simulationType === 'Random' && simulationsEnabled && (
                            <Grid item xs={12}>
                                <Autocomplete
                                    size="small"
                                    options={collectionData || []}
                                    getOptionLabel={(option) => option.collectionName}
                                    value={collectionData?.find(c => c.collectionName === formState.contentName) || null}
                                    onChange={(event, newValue) => {
                                        const syntheticEvent = {
                                            target: {
                                                name: 'selectedCollectionId',
                                                value: newValue?.collectionName || ''
                                            }
                                        };
                                        handleCollectionSelection(syntheticEvent);
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Select Collection"
                                            required
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => option.collectionName === value?.collectionName}
                                />
                            </Grid>
                        )}

                        {/* Date Selection */}
                        <Grid item xs={12}>
                            <TextField
                                size="small"
                                name="scheduledFor"
                                label="Scheduled For"
                                type="date"
                                value={formState.scheduledFor}
                                onChange={handleFormChange}
                                disabled={true}
                                fullWidth
                                required
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onDelete} color="error" startIcon={<DeleteIcon />} sx={{ mr: 'auto' }}>
                    Delete
                </Button>
                <Button onClick={onClose} variant="contained" color="warning">
                    Cancel
                </Button>
                <Button onClick={onEnterMoveMode} variant="contained" color="secondary" startIcon={<DriveFileMoveIcon />}>
                    Move Item
                </Button>
                <Button
                    onClick={onUpdate}
                    variant="contained"
                    color="primary"
                    disabled={noneEnabled || !formState.contentId || !formState.scheduledFor}
                >
                    Update
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditItemModal;